/**
 * Install-to-home-screen state.
 *
 * Two reasons this matters more here than on a normal site, both from
 * COMPLIANCE.md: installing exempts the app from iOS evicting its storage after
 * a week without a visit (hazard H3), and a home-screen icon with quick-add
 * shortcuts is what makes the ten-second target reachable at all.
 *
 * Kept as plain functions with no React in them, so the rules about *when* to
 * ask can be tested without rendering anything.
 */

export type InstallPlatform = 'ios' | 'prompt' | 'installed' | 'unsupported';

/** After this many entries, offer once. Never before — see below. */
export const PROMPT_AFTER_ENTRIES = 5;

export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;

  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    // iOS Safari's own flag, which predates the standard and is still the only
    // way to know on an iPhone.
    ('standalone' in window.navigator && window.navigator.standalone === true)
  );
}

export function isIos(): boolean {
  if (typeof navigator === 'undefined') return false;

  const ua = navigator.userAgent;
  // iPadOS 13+ reports itself as a Mac; the touch points give it away.
  return /iPad|iPhone|iPod/.test(ua) || (ua.includes('Macintosh') && navigator.maxTouchPoints > 1);
}

/**
 * Whether to show the prompt.
 *
 * Deliberately conservative. Never on first load — someone who has just signed
 * up has no reason to trust the app with a home-screen slot yet, and an install
 * banner over an empty logbook is the kind of thing that gets an app deleted.
 * After five entries they have decided it is useful, and by then they have work
 * on the device worth protecting.
 */
export function shouldOfferInstall(input: {
  standalone: boolean;
  dismissed: boolean;
  entryCount: number;
  /** Settings shows it whenever it applies; the automatic offer waits. */
  force?: boolean;
}): boolean {
  if (input.standalone) return false;
  if (input.force === true) return !input.dismissed;

  return !input.dismissed && input.entryCount >= PROMPT_AFTER_ENTRIES;
}
